import { useState } from "react";
import { Link, useParams } from "react-router";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Input,
  LoadingBlock,
  Mono,
  PageHeader,
  Stat,
  StatGroup,
  StatusBadge,
  Table,
  Tag,
  Td,
  Th,
} from "../../components/ui";
import { ApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { formatCount, formatMoney } from "../../lib/money";
import { COMMISSION_STATES, PAYOUT_STATES, REFERRAL_STATES } from "../../lib/states";
import { useAdminAffiliate, useVoidCommission } from "../../lib/queries";
import type { ClickSource, Commission } from "../../lib/types";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

/**
 * One affiliate, in full — which is to say, the fraud review.
 *
 * This screen exists because approving a payout is irreversible and the queue
 * row cannot carry enough to decide on. What it adds is the *traffic*: where
 * the clicks came from, over what window, and whether the shape of that is
 * somebody promoting Maxlayer or somebody manufacturing signups.
 *
 * Everything arrives in one response rather than five. Each round trip to this
 * service costs a Postgres connection — see the handshake note in
 * `content-api` — and five of them for one screen is the cost the read cache
 * exists to avoid paying.
 *
 * ---------------------------------------------------------------------------
 * What is deliberately not here
 * ---------------------------------------------------------------------------
 * No customer is named. Not the account that signed up, not what they spent,
 * not their country — only that a referral converted and what the affiliate
 * earned from it. An affiliate admin is not staff of the platform, and the
 * linkage between a Maxlayer customer and a handle in this programme stays
 * one-directional whoever is asking. The API sends none of those fields, so
 * this is a property of the boundary and not of the markup.
 */
export function AdminAffiliatePage() {
  const { t } = useTranslation("admin");
  const { t: ts } = useTranslation("states");
  const { affiliateId } = useParams();
  const detail = useAdminAffiliate(affiliateId ?? "");

  if (detail.isPending) return <LoadingBlock />;
  if (detail.error) return <Alert>{detail.error.message}</Alert>;

  const { affiliate, links, clickSources, referrals, commissions, payouts } = detail.data;
  const paused = affiliate.status === "paused" || affiliate.status === "banned";

  return (
    <>
      <PageHeader
        title={affiliate.displayName || t("affiliate.fallbackName")}
        description={
          <>
            {t("affiliate.joined", { date: formatDate(affiliate.joinedAt) })}
            {affiliate.payoutAddress ? (
              <span className="font-mono break-all">{affiliate.payoutAddress}</span>
            ) : (
              t("affiliate.noWallet")
            )}
            {affiliate.payoutAddressConfirmed && t("affiliate.confirmed")}
          </>
        }
        action={
          <div className="flex flex-wrap items-center gap-2">
            {affiliate.role === "admin" && <Tag>{t("affiliates.role.admin")}</Tag>}
            {paused && <StatusBadge tone="warn" label={t(`affiliates.status.${affiliate.status}`)} />}
          </div>
        }
      />

      {/* The reason they were paused, at the top and not in a table. It is the
          first thing anybody opening a paused account came to read. */}
      {affiliate.statusReason && <Alert tone="warn">{affiliate.statusReason}</Alert>}

      <StatGroup>
        <Stat boxed={false} size="lg" label={t("affiliate.earned")} value={formatMoney(affiliate.earnedMicros)} />
        <Stat
          boxed={false}
          size="lg"
          label={t("affiliate.paidOut")}
          value={formatMoney(affiliate.paidMicros)}
          tone={affiliate.paidMicros === 0 ? "muted" : "default"}
        />
        <Stat
          boxed={false}
          size="lg"
          label={t("affiliate.signups")}
          value={formatCount(affiliate.referrals)}
          hint={t("affiliate.converted", { count: formatCount(affiliate.conversions) })}
        />
        <Stat
          boxed={false}
          size="lg"
          label={t("affiliate.links")}
          value={formatCount(affiliate.links)}
          tone="muted"
        />
      </StatGroup>

      <ClickSources sources={clickSources} />

      <Card>
        <CardHeader title={t("affiliate.links")} />
        {links.length === 0 ? (
          <EmptyState title={t("affiliate.noLinks")} description={t("affiliate.noLinksDescription")} />
        ) : (
          <Table
            head={
              <tr>
                <Th>{t("affiliate.th.code")}</Th>
                <Th priority="md">{t("affiliate.th.label")}</Th>
                <Th className="text-right">{t("affiliate.th.clicks")}</Th>
                <Th priority="sm" className="text-right">
                  {t("affiliate.th.signups")}
                </Th>
                <Th className="text-right">{t("affiliate.th.earned")}</Th>
              </tr>
            }
          >
            {links.map((link) => (
              <tr key={link.id}>
                <Td>
                  <div className="flex items-center gap-2">
                    <Tag>{link.code}</Tag>
                    {link.archived && <span className="text-[12px] text-fg-subtle">{t("affiliate.archived")}</span>}
                  </div>
                </Td>
                <Td priority="md" className="text-fg-muted">
                  {link.label || "—"}
                </Td>
                <Td className="text-right tabular-nums">{formatCount(link.clicks)}</Td>
                <Td priority="sm" className="text-right tabular-nums">
                  {formatCount(link.referrals)}
                </Td>
                <Td className="text-right tabular-nums">{formatMoney(link.earnedMicros)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Commissions rows={commissions} />

      <Card>
        <CardHeader title={t("affiliate.payouts")} />
        {payouts.length === 0 ? (
          <EmptyState title={t("affiliate.noPayouts")} description={t("affiliate.noPayoutsDescription")} />
        ) : (
          <Table
            head={
              <tr>
                <Th>{t("affiliate.th.requested")}</Th>
                <Th>{t("affiliate.th.status")}</Th>
                <Th className="text-right">{t("affiliate.th.amount")}</Th>
              </tr>
            }
          >
            {payouts.map((payout) => {
              const state = PAYOUT_STATES[payout.status];
              return (
                <tr key={payout.id}>
                  <Td>{formatDate(payout.requestedAt)}</Td>
                  <Td>
                    <div className="space-y-0.5">
                      <StatusBadge tone={state.tone} label={ts(state.label)} />
                      {payout.failureReason && (
                        <p className="text-[12px] text-fg-subtle">{payout.failureReason}</p>
                      )}
                    </div>
                  </Td>
                  <Td className="text-right tabular-nums">
                    {formatMoney(payout.amountMicros, payout.currency)}
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
      </Card>

      <Card>
        <CardHeader title={t("affiliate.signupsTitle")} description={t("affiliate.signupsDescription")} />
        {referrals.length === 0 ? (
          <EmptyState title={t("affiliate.noSignups")} description={t("affiliate.noSignupsDescription")} />
        ) : (
          <Table
            head={
              <tr>
                <Th>{t("affiliate.th.signedUp")}</Th>
                <Th priority="sm">{t("affiliate.th.link")}</Th>
                <Th>{t("affiliate.th.status")}</Th>
                <Th className="text-right">Commission</Th>
              </tr>
            }
          >
            {referrals.map((referral) => {
              const state = REFERRAL_STATES[referral.status];
              return (
                <tr key={referral.id}>
                  <Td>{formatDate(referral.signedUpAt)}</Td>
                  <Td priority="sm">
                    <Tag>{referral.linkCode}</Tag>
                  </Td>
                  <Td>
                    <StatusBadge tone={state.tone} label={ts(state.label)} />
                  </Td>
                  <Td className="text-right tabular-nums">
                    {referral.commissionMicros === null ? (
                      <span className="text-fg-subtle">—</span>
                    ) : (
                      formatMoney(referral.commissionMicros)
                    )}
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
      </Card>

      <p className="text-[12px] text-fg-subtle">
        <Link className="transition-colors duration-200 ease-smooth hover:text-accent" to="/admin/affiliates">
          {t("affiliate.allAffiliates")}
        </Link>
      </p>
    </>
  );
}

/**
 * Where the traffic came from, which is the whole reason this screen exists.
 *
 * What is being read is a *shape*, not any single row. Real promotion looks
 * like a handful of hosts spread over weeks. Cookie stuffing looks like one
 * host, one country, and a thousand clicks inside an hour — so the window
 * between the first and last click is given as prominently as the count, and
 * the rows that fit that shape are marked rather than left to be spotted.
 *
 * A null referrer is *not* itself suspicious and is not marked: it is what a
 * direct visit, a link in an app, and a browser stripping the header all look
 * like. It only means something next to a tight window.
 */
function ClickSources({ sources }: { sources: ClickSource[] }) {
  const { t } = useTranslation("admin");
  return (
    <Card>
      <CardHeader
        title={t("affiliate.traffic")}
        description={t("affiliate.trafficDescription")}
      />
      {sources.length === 0 ? (
        <EmptyState title={t("affiliate.noClicks")} description={t("affiliate.noClicksDescription")} />
      ) : (
        <Table
          head={
            <tr>
              <Th>{t("affiliate.th.source")}</Th>
              <Th priority="sm">{t("affiliate.th.link")}</Th>
              <Th priority="md">{t("affiliate.th.country")}</Th>
              <Th>{t("affiliate.th.window")}</Th>
              <Th className="text-right">{t("affiliate.th.clicks")}</Th>
            </tr>
          }
        >
          {sources.map((source) => {
            const key = `${source.linkCode}:${source.referrerHost ?? ""}:${source.country ?? ""}`;
            const hours =
              (new Date(source.lastAt).getTime() - new Date(source.firstAt).getTime()) / 3_600_000;
            /*
             * Twenty clicks inside an hour from one source. Not a verdict and
             * not an action — it marks the row an admin should look at, which
             * is the whole job of this column. The threshold is deliberately
             * loose: a false positive costs somebody ten seconds, and a missed
             * one costs a payout.
             */
            const burst = source.clicks >= 20 && hours < 1;

            return (
              <tr key={key}>
                <Td>
                  {source.referrerHost ? (
                    <Mono className="text-fg">{source.referrerHost}</Mono>
                  ) : (
                    <span className="text-[13px] text-fg-subtle">Direct or unknown</span>
                  )}
                </Td>
                <Td priority="sm">
                  <Tag>{source.linkCode}</Tag>
                </Td>
                <Td priority="md" className="text-fg-muted">
                  {source.country ?? "—"}
                </Td>
                <Td
                  className="text-fg-muted"
                  title={t("affiliate.range", { from: formatDate(source.firstAt), to: formatDate(source.lastAt) })}
                >
                  {formatWindow(hours, t)}
                </Td>
                <Td className="text-right tabular-nums">
                  <div className="flex items-center justify-end gap-2">
                    {burst && <StatusBadge tone="warn" label={t("affiliate.burst")} />}
                    <span>{formatCount(source.clicks)}</span>
                  </div>
                </Td>
              </tr>
            );
          })}
        </Table>
      )}
    </Card>
  );
}

/** How long a source has been sending traffic, at the resolution that matters. */
function formatWindow(hours: number, t: TFunction<"admin">): string {
  if (hours < 1) return t("affiliate.window.underHour");
  if (hours < 48) return t("affiliate.window.hours", { count: Math.round(hours) });
  return t("affiliate.window.days", { count: Math.round(hours / 24) });
}

/**
 * What they earned, and the one enforcement action on this screen.
 *
 * Voiding takes a reason and no confirmation dialog, which is the opposite of
 * the call the affiliate list makes about pausing. Pausing is undone in one
 * click from the same row; this is not undoable from any screen — so the
 * friction is the reason box, which is a better guard than a modal precisely
 * because it cannot be dismissed by reflex. The reason is stored on the
 * commission and shown to the affiliate.
 *
 * A voided commission leaves their balance. It does not become re-earnable:
 * the referral goes back to `signed_up`, but one commission per referral is a
 * unique constraint, so a second conversion for that customer is answered
 * `duplicate` and changes nothing.
 */
function Commissions({ rows }: { rows: Commission[] }) {
  const { t } = useTranslation("admin");
  const { t: ts } = useTranslation("states");
  const voidIt = useVoidCommission();
  const [voiding, setVoiding] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function submit(commissionId: string) {
    setError(null);
    try {
      await voidIt.mutateAsync({ commissionId, reason: reason.trim() });
      setVoiding(null);
      setReason("");
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("affiliate.failed"));
    }
  }

  return (
    <Card>
      <CardHeader
        title={t("affiliate.commissions")}
        description={t("affiliate.commissionsDescription")}
      />
      {error && (
        <div className="px-4 pt-4">
          <Alert>{error}</Alert>
        </div>
      )}
      {rows.length === 0 ? (
        <EmptyState title={t("affiliate.nothingEarned")} description={t("affiliate.nothingEarnedDescription")} />
      ) : (
        <Table
          head={
            <tr>
              <Th>{t("affiliate.th.earned")}</Th>
              <Th priority="md">{t("affiliate.th.link")}</Th>
              <Th>{t("affiliate.th.status")}</Th>
              <Th className="text-right">{t("affiliate.th.amount")}</Th>
              <Th className="text-right" />
            </tr>
          }
        >
          {rows.map((commission) => {
            const state = COMMISSION_STATES[commission.status];
            /*
             * Only what has not been sent. A paid commission is money that has
             * gone, and the API refuses to void one — this is the screen
             * agreeing with it rather than offering a button that fails.
             */
            const voidable = commission.status === "pending" || commission.status === "payable";
            const open = voiding === commission.id;

            return (
              <tr key={commission.id}>
                <Td>{formatDate(commission.earnedAt)}</Td>
                <Td priority="md">
                  <Tag>{commission.linkCode}</Tag>
                </Td>
                <Td>
                  <div className="space-y-0.5">
                    <StatusBadge tone={state.tone} label={ts(state.label)} />
                    {commission.resolutionReason && (
                      <p className="text-[12px] text-fg-subtle">{commission.resolutionReason}</p>
                    )}
                  </div>
                </Td>
                <Td className="text-right tabular-nums">
                  <span
                    className={
                      commission.status === "reversed" || commission.status === "void"
                        ? "text-fg-subtle line-through"
                        : undefined
                    }
                  >
                    {formatMoney(commission.amountMicros, commission.currency)}
                  </span>
                </Td>
                <Td className="text-right">
                  {!voidable ? null : open ? (
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Input
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        placeholder={t("affiliate.reasonPlaceholder")}
                        className="h-8 w-56"
                        maxLength={500}
                        autoFocus
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        loading={voidIt.isPending}
                        disabled={!reason.trim()}
                        onClick={() => void submit(commission.id)}
                      >
                        {t("affiliate.void")}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setVoiding(null)}>
                        {t("affiliate.cancel")}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setVoiding(commission.id);
                        setReason("");
                        setError(null);
                      }}
                    >
                      {t("affiliate.void")}
                    </Button>
                  )}
                </Td>
              </tr>
            );
          })}
        </Table>
      )}
    </Card>
  );
}
